<template>
    <div class="esign-settings-providers">
        <span class="mb-4 fs-5 bold">
            {{ $l10n.__('Service de signature électronique') }}
        </span>
        <span class="semibold mb-2 fs-3 lh-20">
            {{ $l10n.__('Faites signer vos documents plus vite et de manière sécurisée') }}
        </span>
        <span class="mb-4">
            {{
                $l10n.__(
                    'Vos destinataires reçoivent votre signature et peuvent apposer leur signature en seulement quelques secondes. Obtenir la signature de documents PDF n’a jamais été aussi simple !',
                )
            }}
        </span>
        <div class="provider-choices flex-row ai-center jc-space">
            <div class="flex-row provider-informations">
                <SaasIcon
                    v-if="providerSelected.name"
                    :icon="providerIconName"
                    class="provider-icon ml-3"
                    height="32"
                    width="32"
                />

                <span class="semibold fs-3 gray-100 lh-20">{{ providerSelected.name }}</span>
                <div class="tag-wrapper">
                    <CompassTag v-if="isProviderSellsy" size="small">
                        {{ $t.__('common.labels.by_default') }}
                    </CompassTag>
                    <CompassTag
                        v-if="!isProviderSellsy"
                        ref="statusProvider"
                        :color="colorLabelStatusDocusign"
                        size="small"
                    >
                        {{ labelStatusDocusign }}
                    </CompassTag>
                </div>
            </div>
            <div class="fright flex-row ai-center">
                <link-button
                    v-if="!isProviderSellsy && isConnected"
                    class="mr-5 btn-check-status"
                    @clicked="onCheckStatus"
                >
                    {{ $l10n.__('Actualiser le statut') }}
                </link-button>
                <CompassButton
                    v-if="!isProviderSellsy"
                    ref="submitBtn"
                    :label="labelSubmitBtn"
                    :type="bkgSubmitBtn"
                    class="mr-3"
                    size="small"
                    @click="onLoginProvider()"
                />
                <CompassButton
                    v-if="!isConnected"
                    ref="changeProviderBtn"
                    :label="$t.__('esign.ESignSettingsProviders.edit_service')"
                    class="mr-3"
                    size="small"
                    type="primary"
                    @click="onModifProvider"
                />
            </div>
        </div>
        <preloader :display="loading" />
    </div>
</template>

<script>
import Preloader from 'vcomponents/utilities/Preloader.vue';
import LinkButton from 'vcomponents/utilities/LinkButton.vue';
import TagRounded from 'vcomponents/utilities/TagRounded.vue';
import ChipLabel from 'vcomponents/utilities/ChipLabel.vue';
import esignStore from 'stores/esignStore';
import { modeEnum } from 'vcomponents/esign/EsignPopupProvider.vue';

import { createNamespacedHelpers } from 'vuex';
import SaasIcon from 'vcomponents/common/SaasIcon.vue';

const esign = createNamespacedHelpers('esign');

const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;
export default {
    components: {
        SaasIcon,
        Preloader,
        TagRounded,
        LinkButton,
        ChipLabel,
    },
    data() {
        return {
            store: esignStore,
            loading: true,
        };
    },
    computed: {
        ...esign.mapGetters(['isConnected', 'providerSelected', 'providers', 'isProviderSellsy']),
        labelStatusDocusign() {
            return this.isConnected ? this.$l10n.__('Connecté') : this.$l10n.__('Déconnecté');
        },
        colorLabelStatusDocusign() {
            return this.isConnected ? 'green' : 'red';
        },
        bkgSubmitBtn() {
            if (this.isProviderSellsy) {
                return 'primary';
            } else {
                return this.isConnected ? 'primary' : 'secondary';
            }
        },
        labelSubmitBtn() {
            if (this.isProviderSellsy) {
                return this.$l10n.__('Modifier le service');
            }
            return this.isConnected ? this.$l10n.__('Déconnexion') : this.$l10n.__('Connexion');
        },
        providerIconName() {
            if (this.providerSelected.name === 'Sellsy') {
                return 'sellsyCloud';
            }
            return 'docusign';
        },
    },
    created() {
        this.$store.registerModule('esign', this.store);
        if (this.$route.query.is_provider_connected) Push.success('Docusign connecté avec succès');
        this.fetchProvider().finally((_) => (this.loading = false));
    },
    beforeDestroy() {
        this.$store.unregisterModule('esign');
    },
    methods: {
        ...esign.mapActions(['fetchProvider']),
        ...popupPanelMapActions(['open']),
        onLoginProvider() {
            this.open({
                props: {
                    mode: this.isConnected ? modeEnum.LOGOUT_PROVIDER : modeEnum.LOGIN_PROVIDER,
                },
                key: 'esignPopupProvider',
            });
        },
        onModifProvider() {
            let mode = null;
            if (this.isProviderSellsy) {
                mode = modeEnum.CHANGE_PROVIDER;
            } else {
                mode = this.isConnected ? modeEnum.LOGOUT_PROVIDER : modeEnum.CHANGE_PROVIDER;
            }
            this.open({
                props: {
                    mode,
                },
                key: 'esignPopupProvider',
            });
        },
        onCheckStatus() {
            this.loading = true;
            this.fetchProvider()
                .then((_) => {
                    Push.success(this.$l10n.__('Le statut a été mis à jour avec succès'));
                })
                .catch((_) => {
                    Push.error(
                        this.$l10n.__('Une erreur est survenue lors de la mise à jour du statut'),
                    );
                })
                .finally((_) => (this.loading = false));
        },
    },
};
</script>

<style lang="scss">
@import '~@sellsy/styleguide/src/_variables';

.esign-settings-providers {
    .provider-choices {
        border: 1px solid $gray-40;
        border-radius: 4px;

        .label-provider {
            margin-top: 12px;
            margin-bottom: 12px;
        }

        .el-button {
            padding: 2px 12px;
        }

        .provider-informations {
            gap: 8px;

            .chip.label-chip {
                margin-left: 91px;
            }

            .provider-icon {
                margin-top: 12px;
                margin-bottom: 12px;
            }

            span {
                margin-bottom: 18px;
                margin-top: 18px;
            }
        }
    }
    .tag-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: center;
    }
}
</style>
